import _get from 'lodash/get';
import _omit from 'lodash/omit';
import {
  AttachmentCuInputObject,
  DocumentationImportCreateInputObject,
  DocumentationImportKindEnum,
  DocumentationRenewalRepetitionEnum,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import DocumentImportFormFieldNames, {
  DocumentImportAiEnhancementOption,
} from '@lib/enums/fieldNames/documentImportFormFieldNames';
import { GetTaskGroupKey } from '../../utils/createTaskGroupKeyResolver';
import buildTaskGroupLinks from '../../utils/buildTaskGroupLinks';
import resolveCategoryPermissionOverride, {
  CategoryOption,
} from '../../utils/resolveCategoryPermissionOverride';

type SelectOption = { value: string; label?: string; visibleViaQr?: boolean };

function stripUrl(attachment: unknown): AttachmentCuInputObject {
  return _omit(
    attachment as Record<string, unknown>,
    'url',
    'urls',
  ) as AttachmentCuInputObject;
}

function toLinks(
  values: Values,
  name: string,
  options?: { withVisibleViaQr?: boolean },
) {
  const list = _get(values, name) as SelectOption[] | undefined;
  if (!list?.length) return undefined;
  return list.map((s) =>
    options?.withVisibleViaQr && s.visibleViaQr !== undefined
      ? { id: s.value, visibleViaQr: s.visibleViaQr }
      : { id: s.value },
  );
}

/**
 * Convert RHF wizard values into a DocumentationImportCreateInputObject.
 * Returns null if the source side of the payload is empty (e.g. no files
 * uploaded for FILES kind), so the caller can skip submission.
 *
 * `getTaskGroupKey` is a defensive Apollo-cache lookup applied to
 * `taskGroupLinks` only. The modal-driven Tasks selector emits group_keys
 * directly, in which case the lookup is a no-op (cache returns the same
 * value back). It only matters when something leaks a per-occurrence
 * Task.id into form state — BE rejects those with "Invalid task ids".
 */
export default function getPreparedImportData(
  values: Values,
  getTaskGroupKey?: GetTaskGroupKey,
  // Set while the company's AI budget refuses spend. The form clears the
  // enhancements as the block appears, so this is the race guard for a budget
  // that runs out between the last render and submit: sending a detect* flag
  // then fails the mutation and creates NO import, losing the files too, while
  // dropping the flags still imports them.
  options?: { aiBlocked?: boolean },
): DocumentationImportCreateInputObject | null {
  const sourceKind = _get(values, [
    DocumentImportFormFieldNames.SourceKind,
    'value',
  ]) as DocumentationImportKindEnum | undefined;

  // Source-specific section
  let source: DocumentationImportCreateInputObject['source'] | null = null;
  if (sourceKind === DocumentationImportKindEnum.Files) {
    const files = (_get(values, DocumentImportFormFieldNames.Files) ||
      []) as unknown[];
    if (!files.length) return null;
    source = { fileAttachmentsAttached: files.map(stripUrl) };
  } else if (sourceKind === DocumentationImportKindEnum.Archive) {
    const archive = (_get(values, DocumentImportFormFieldNames.Archive) ||
      []) as unknown[];
    if (!archive.length) return null;
    const [first] = archive;
    source = { archiveAttachmentAttached: stripUrl(first) };
  } else if (sourceKind === DocumentationImportKindEnum.Ftp) {
    const url = (_get(values, DocumentImportFormFieldNames.FtpUrl) ||
      '') as string;
    if (!url.trim()) return null;
    const username = (_get(values, DocumentImportFormFieldNames.FtpUsername) ||
      '') as string;
    const password = (_get(values, DocumentImportFormFieldNames.FtpPassword) ||
      '') as string;
    source = {
      ftpConfig: {
        url: url.trim(),
        ...(username ? { username } : {}),
        ...(password ? { password } : {}),
      },
    };
  } else {
    return null;
  }

  const attributes: DocumentationImportCreateInputObject = {
    kind: sourceKind,
    source,
  };

  // AI prefill options (step 2). This field is only rendered when
  // AiAssistance is enabled, so an empty/absent selection leaves
  // `detectFields` off entirely and the BE imports without AI detection.
  // `codePrompt` applies only when code detection is selected.
  const aiEnhancements = options?.aiBlocked
    ? []
    : ((_get(values, DocumentImportFormFieldNames.AiEnhancements) ||
        []) as SelectOption[]);
  const hasAiEnhancement = (option: DocumentImportAiEnhancementOption) =>
    aiEnhancements.some((item) => item.value === option);

  const detectFields: NonNullable<
    DocumentationImportCreateInputObject['detectFields']
  > = {};
  if (hasAiEnhancement(DocumentImportAiEnhancementOption.DetectName)) {
    detectFields.name = true;
  }
  if (hasAiEnhancement(DocumentImportAiEnhancementOption.DetectCategory)) {
    detectFields.category = true;
  }
  if (hasAiEnhancement(DocumentImportAiEnhancementOption.DetectDescription)) {
    detectFields.description = true;
  }
  if (hasAiEnhancement(DocumentImportAiEnhancementOption.DetectCode)) {
    detectFields.code = true;
    const codePrompt = (_get(
      values,
      DocumentImportFormFieldNames.AiCodeInstructions,
    ) || '') as string;
    if (codePrompt.trim()) {
      detectFields.codePrompt = codePrompt.trim();
    }
  }
  if (hasAiEnhancement(DocumentImportAiEnhancementOption.DetectTags)) {
    detectFields.tags = true;
  }
  if (Object.keys(detectFields).length) {
    attributes.detectFields = detectFields;
  }

  // Shared metadata — Category
  const category = _get(values, DocumentFormFieldNames.Category) as
    CategoryOption | undefined;
  if (category?.value) {
    attributes.categoryId = category.value;
  }

  // "Allow download"/"Allow printing" checkboxes, prefilled from the
  // selected category (see getCategoryFields.tsx). An override applies to
  // every document in the batch. The wizard seeds no defaults for these, and
  // the whole category block is hidden without categories access — so an
  // untouched checkbox stays `undefined` and is left out entirely rather than
  // stamping an explicit `false` on every imported document.
  const allowDownloadRaw = _get(
    values,
    DocumentFormFieldNames.AllowDownload,
  ) as boolean | undefined;
  const allowPrintRaw = _get(values, DocumentFormFieldNames.AllowPrint) as
    boolean | undefined;
  if (allowDownloadRaw !== undefined) {
    attributes.allowDownload = resolveCategoryPermissionOverride(
      category,
      'allowDownload',
      allowDownloadRaw,
    );
  }
  if (allowPrintRaw !== undefined) {
    attributes.allowPrint = resolveCategoryPermissionOverride(
      category,
      'allowPrint',
      allowPrintRaw,
    );
  }

  // Approver
  const approver = _get(values, DocumentFormFieldNames.Approver) as
    SelectOption | undefined;
  if (approver?.value) {
    attributes.approverId = approver.value;
  }

  // Link arrays
  const sectorLinks = toLinks(values, DocumentFormFieldNames.Sectors);
  if (sectorLinks) attributes.sectorLinks = sectorLinks;

  const siteLinks = toLinks(values, DocumentFormFieldNames.Sites);
  if (siteLinks) attributes.siteLinks = siteLinks;

  const assetLinks = toLinks(values, DocumentFormFieldNames.Equipment, {
    withVisibleViaQr: true,
  });
  if (assetLinks) attributes.assetLinks = assetLinks;

  const areaLinks = toLinks(values, DocumentFormFieldNames.Areas, {
    withVisibleViaQr: true,
  });
  if (areaLinks) attributes.areaLinks = areaLinks;

  const formTemplateLinks = toLinks(
    values,
    DocumentFormFieldNames.FormTemplates,
  );
  if (formTemplateLinks) attributes.formTemplateLinks = formTemplateLinks;

  const taskGroupLinks = buildTaskGroupLinks({
    values: _get(values, DocumentFormFieldNames.Tasks) as
      SelectOption[] | undefined,
    getTaskGroupKey,
  });
  if (taskGroupLinks) attributes.taskGroupLinks = taskGroupLinks;

  // Renewal reminder
  const renewalEnabled = !!_get(
    values,
    DocumentFormFieldNames.RenewalReminderEnabled,
  );
  if (renewalEnabled) {
    const deadline = _get(
      values,
      DocumentFormFieldNames.RenewalReminderDeadlineDate,
    ) as string | undefined;
    if (deadline) {
      const [datePart] = deadline.split('T');
      attributes.renewalDeadlineDate = datePart;
    }
    const repetition = _get(values, [
      DocumentFormFieldNames.RenewalReminderRepetition,
      'value',
    ]) as DocumentationRenewalRepetitionEnum | undefined;
    if (repetition) {
      attributes.renewalRepetition = repetition;
      if (repetition === DocumentationRenewalRepetitionEnum.Custom) {
        const customDays = _get(
          values,
          DocumentFormFieldNames.RenewalReminderCustomDays,
        ) as string | number | undefined;
        const parsed = customDays ? Number(customDays) : NaN;
        if (!Number.isNaN(parsed)) {
          attributes.renewalRepetitionCustomDays = parsed;
        }
      }
    }
    const earlyAmount = _get(
      values,
      DocumentFormFieldNames.RenewalReminderEarlyAmount,
    ) as string | number | undefined;
    const parsedEarly = earlyAmount ? Number(earlyAmount) : NaN;
    if (!Number.isNaN(parsedEarly)) {
      attributes.renewalEarlyReminderDays = parsedEarly;
    }
  }

  return attributes;
}
